import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { listTenants } from "@/api/tenants";
import { LoadingRegion, Skeleton } from "@/components/ui";

import { TreeNode } from "./TreeNode";

const FOCUSABLE = '[data-tree-item="true"]';

export function ResourceTree() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";

  // Same key as the Tenants page → one shared cache entry. The always-mounted
  // sidebar keeps this warm for every page.
  const query = useQuery({
    queryKey: ["tenants", "active"],
    queryFn: () => listTenants(token),
    enabled: !!token,
  });

  const treeRef = useRef<HTMLUListElement>(null);

  // Container-level roving: ArrowUp/Down move focus across the visible rows
  // (expand/collapse is handled per-node via ArrowRight/Left).
  function onKeyDown(e: React.KeyboardEvent) {
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(e.key)) return;
    const items = Array.from(
      treeRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
    );
    if (items.length === 0) return;
    e.preventDefault();
    const idx = items.indexOf(document.activeElement as HTMLElement);
    let next = idx;
    if (e.key === "ArrowDown") next = idx < 0 ? 0 : Math.min(idx + 1, items.length - 1);
    else if (e.key === "ArrowUp") next = idx <= 0 ? 0 : idx - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    items[next]?.focus();
  }

  const items = query.data?.items ?? [];

  return (
    <div>
      {/* Named like the groups of entries above it (same type, same left edge), and it says
          what the tree is: the customers' infrastructure, tenant by tenant, environment by
          environment. */}
      <p className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Infrastructure
      </p>

      {query.isLoading && (
        <LoadingRegion className="space-y-1.5 px-2 py-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </LoadingRegion>
      )}

      {query.isError && (
        <div className="px-2 py-1 text-xs text-danger-fg">
          Unable to load.{" "}
          <button
            type="button"
            onClick={() => query.refetch()}
            className="underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {!query.isLoading && !query.isError && items.length === 0 && (
        <p className="px-2 py-1 text-xs text-ink-muted">No tenants</p>
      )}

      {items.length > 0 && (
        <ul
          ref={treeRef}
          role="tree"
          aria-label="Tenants and environments"
          onKeyDown={onKeyDown}
          className="flex flex-col"
        >
          {items.map((tenant) => (
            <TreeNode key={tenant.tenantId} tenant={tenant} />
          ))}
        </ul>
      )}
    </div>
  );
}
